import { Capacitor } from '@capacitor/core'
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics'
import type { NavigationFailure, RouteLocationNormalized } from 'vue-router'

import { CUSTOM_EVENTS, logEvent } from '@/firebase-analytics'
import { ABSENCE } from '@/modules/absence/routes'
import { APPOINTMENTS } from '@/modules/appointments/routes'
import { useAuthStore } from '@/modules/auth/store'
import { HISTORY } from '@/modules/history/routes'
import { INFO, INFO_DETAILS } from '@/modules/info/routes'
import { MEAL } from '@/modules/meal/routes'
import { NEWS, NEWS_DETAILS } from '@/modules/news/routes'
import {
  BSCL_DETAILS,
  EPISODE_OF_CARE_DETAILS,
  EPISODES_OF_CARE,
  SURVEY_DETAILS,
  SURVEYS,
} from '@/modules/survey/routes'
import { ENABLE_ANALYTICS } from '@/settings'

const featureRoutes = [APPOINTMENTS, NEWS, INFO, SURVEYS, HISTORY, MEAL, ABSENCE]

// Map routes to their parent modules for detecting intra-module navigation
const moduleRoutes: Record<string, string> = {
  [SURVEYS]: SURVEYS,
  [EPISODES_OF_CARE]: SURVEYS,
  [EPISODE_OF_CARE_DETAILS]: SURVEYS,
  [SURVEY_DETAILS]: SURVEYS,
  [BSCL_DETAILS]: SURVEYS,
  [NEWS]: NEWS,
  [NEWS_DETAILS]: NEWS,
  [INFO]: INFO,
  [INFO_DETAILS]: INFO,
  [APPOINTMENTS]: APPOINTMENTS,
  [HISTORY]: HISTORY,
  [MEAL]: MEAL,
  [ABSENCE]: ABSENCE,
}

function getModuleForRoute(routeName: string | symbol | null | undefined): string | null {
  if (!routeName) return null
  return moduleRoutes[routeName as string] || null
}

export const gaMiddleware = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  failure: NavigationFailure | void
) => {
  // Don't log if navigation failed
  if (failure) {
    return
  }

  // Early return if user is not logged in
  const authStore = useAuthStore()
  if (!authStore.user) {
    return
  }

  // Get module for target route
  const toModule = getModuleForRoute(to.name)
  const fromModule = getModuleForRoute(from.name)

  // Only log for routes that belong to a feature module
  if (!toModule) {
    return
  }

  // Don't log if navigating within the same module (e.g., SURVEYS -> SURVEYS, or SURVEYS -> EPISODES_OF_CARE)
  if (toModule && fromModule && toModule === fromModule) {
    return
  }

  // For sub-routes (like SURVEY_DETAILS), use the parent module name for MODULE_VISITED
  const moduleName = featureRoutes.includes(to.name as string) ? to.name : toModule

  // Set screen name for native platforms (web deprecated)
  if (ENABLE_ANALYTICS && Capacitor.isNativePlatform()) {
    FirebaseAnalytics.setScreenName({
      screenName: String(to.name),
      nameOverride: `${String(to.name)}Screen`,
    }).catch(() => {
      // Ignore screen name errors
    })
  }

  // Fire and forget - don't block navigation
  // Use void to explicitly ignore promise (fire and forget)
  void logEvent(CUSTOM_EVENTS.MODULE_VISITED, {
    module: moduleName as string,
  })
}
